import { Link, useNavigate } from "@tanstack/react-router"
import { Search } from "lucide-react"

import { useScheduleActions } from "../../hooks/use-schedule-actions"
import {
  cadenceLabel,
  scheduledJobLabel,
  scheduledJobGroup,
  SCHEDULE_JOB_GROUPS,
  scheduleDuration,
  scheduleRelative,
  scheduleShortDate,
} from "../../lib/scheduled-job-display"
import { cn } from "../../lib/utils"
import type { ScheduledJob } from "../../services/use-scheduled-jobs"
import { Table, type TableColumn } from "../ui/table"
import { Switch } from "../ui/switch"
import { SegmentedControl } from "../ui/segmented-control"
import { ScheduleJobStatus, scheduleJobStatus } from "./schedule-job-status"

type SearchState = { filter: "all" | "failing" | "paused"; q: string }
type Props = {
  jobs: readonly ScheduledJob[]
  search: SearchState
  onSearch: (search: SearchState) => void
  canEdit: boolean
}

export function ScheduleJobList({ jobs, search, onSearch, canEdit }: Props) {
  const navigate = useNavigate()
  const actions = useScheduleActions()
  const counts = {
    all: jobs.length,
    failing: jobs.filter((job) => scheduleJobStatus(job) === "failing").length,
    paused: jobs.filter((job) => !job.enabled).length,
  }
  const filtered = jobs.filter(
    (job) =>
      (search.filter === "all" || scheduleJobStatus(job) === search.filter) &&
      `${scheduledJobLabel(job.id)} ${job.id}`.toLowerCase().includes(search.q.trim().toLowerCase())
  )
  const grouped = SCHEDULE_JOB_GROUPS.flatMap((group) =>
    filtered.filter((job) => scheduledJobGroup(job.id) === group)
  )
  return (
    <>
      <div className="list-head">
        <div>
          <h2 className="list-title">Scheduled jobs</h2>

          <p className="summary tnum">
            {`${counts.all} jobs · ${counts.all - counts.paused} active · ${counts.paused} paused`}

            {counts.failing ? (
              <span className="c-destructive">{` · ${counts.failing} failing`}</span>
            ) : null}
          </p>
        </div>

        <div className="toolbar">
          <SegmentedControl
            className="seg"
            label="Filter jobs"
            value={search.filter}
            options={[
              { value: "all", label: "All", count: counts.all },
              { value: "failing", label: "Failing", count: counts.failing },
              { value: "paused", label: "Paused", count: counts.paused },
            ]}
            onChange={(filter) => onSearch({ ...search, filter })}
          />

          <label className="search">
            <span className="sr-only">Search jobs</span>

            <Search className="icon" />

            <input
              type="search"
              className="input"
              placeholder="Search jobs"
              value={search.q}
              onChange={(event) => onSearch({ ...search, q: event.target.value })}
            />
          </label>
        </div>
      </div>

      {actions.error ? (
        <p className="banner" role="alert">
          {actions.error}
        </p>
      ) : null}

      <section className="card overflow-hidden" aria-label="Scheduled jobs">
        <Table
          columns={columns(search, canEdit, actions)}
          rows={grouped}
          rowKey={(job) => job.id}
          embedded
          className="table-shell"
          tableClassName="jobs"
          rowLinkTarget
          rowSeparator={(job, previous) =>
            scheduledJobGroup(job.id) !== (previous ? scheduledJobGroup(previous.id) : null)
              ? scheduledJobGroup(job.id)
              : null
          }
          rowClassName={(job) => cn("job-row", !job.enabled && "paused")}
          onRowClick={(job) =>
            void navigate({ to: "/settings/schedules/$jobId", params: { jobId: job.id }, search })
          }
          empty={
            <>
              {`No jobs match “${search.q || search.filter}”. `}

              <button className="btn ghost" onClick={() => onSearch({ q: "", filter: "all" })}>
                Clear filters
              </button>
            </>
          }
        />
      </section>
    </>
  )
}

function columns(
  search: SearchState,
  canEdit: boolean,
  actions: ReturnType<typeof useScheduleActions>
): TableColumn<ScheduledJob>[] {
  return [
    {
      key: "job",
      header: "Job",
      render: (job) => (
        <>
          <div className="od-cluster">
            <Link
              className="job-link"
              to="/settings/schedules/$jobId"
              params={{ jobId: job.id }}
              search={search}
            >
              {scheduledJobLabel(job.id)}
            </Link>

            {scheduleJobStatus(job) !== "active" ? <ScheduleJobStatus job={job} /> : null}
          </div>

          <div className="sub mono">{`${job.handler.key}@${job.handler.version}`}</div>
        </>
      ),
    },
    {
      key: "schedule",
      header: "Schedule",
      className: "col-sched",
      render: (job) => (
        <>
          <div className="sched">{cadenceLabel(job.schedule.cron)}</div>

          <div className="sub">
            <code>{job.schedule.cron}</code>

            {` · ${job.schedule.timeZone}`}
          </div>
        </>
      ),
    },
    {
      key: "next",
      header: "Next run",
      className: "col-next",
      render: (job) =>
        job.enabled && !job.manualOnly && job.nextRunAt ? (
          <>
            <div className="tnum od-nowrap">
              {scheduleRelative(job.nextRunAt, job.schedule.timeZone)}
            </div>

            <div className="sub tnum od-nowrap">
              {scheduleShortDate(job.nextRunAt, job.schedule.timeZone)}
            </div>
          </>
        ) : (
          <>
            <div className="muted">—</div>

            <div className="sub">{job.manualOnly ? "Manual only" : "Paused"}</div>
          </>
        ),
    },
    {
      key: "recent",
      header: "Last 20 runs",
      className: "col-ticks",
      render: (job) => <RecentTicks job={job} />,
    },
    {
      key: "average",
      header: "Avg duration",
      className: "col-avg tnum",
      render: (job) => scheduleDuration(job.stats.avgDurationMs),
    },
    {
      key: "active",
      header: "Active",
      className: "col-switch",
      render: (job) => (
        <span className="switch-hit" title={job.manualOnly ? "This job is manual only" : undefined}>
          <Switch
            className="switch"
            id={`active-${job.id}`}
            label={`${job.enabled ? "Pause" : "Resume"} ${scheduledJobLabel(job.id)}`}
            checked={job.enabled}
            disabled={!canEdit || job.manualOnly || actions.pendingId !== null}
            onCheckedChange={() => actions.toggle(job)}
          />
        </span>
      ),
    },
  ]
}
function RecentTicks({ job }: { job: ScheduledJob }) {
  const runs = job.recentRuns.slice(0, 20).toReversed()
  const label = `${runs.filter((run) => run.status === "succeeded").length}/${runs.length} succeeded`
  return (
    <>
      <div className="ticks" role="img" aria-label={label}>
        {Array.from({ length: 20 - runs.length }, (_, index) => (
          <span key={`empty-${index}`} className="tick" />
        ))}

        {runs.map((run, index) => (
          <span
            key={index}
            className={cn("tick", {
              ok: run.status === "succeeded",
              fail: run.status === "failed" || run.status === "interrupted",
              run: run.status === "running",
              queued: run.status === "queued",
            })}
            title={`${run.status} · ${scheduleShortDate(run.triggeredAt, job.schedule.timeZone)}`}
          />
        ))}
      </div>

      <div className="sub tnum">{label}</div>
    </>
  )
}
